/**
 * usePermissions
 * ═══════════════════════════════════════════════════════════════
 * Central hook for effective-permission checks on the client.
 *
 * Why this exists (P9/P10 refactor, 2026-04-20):
 *   Before this, the client had permissions hardcoded in TWO places:
 *     1. Sidebar.jsx had a PATH_PERMISSIONS map + ROLE_PERMISSIONS defaults
 *     2. RoleRoute.jsx used role-hierarchy only, ignoring custom roles entirely
 *   That meant custom roles created via the RBAC portal — the whole point of
 *   the RBAC module — had no effect on the client. Custom permission grants
 *   and denies were completely bypassed.
 *
 * What it does:
 *   Reads the effective permission set for the current user from
 *   GET /rbac/me/permissions (served by rbac.service.getUserPermissionProfile).
 *   Returns:
 *     - { has(perm), hasAny(perms[]), hasAll(perms[]) } — predicates
 *     - permissions: string[]                          — flat list
 *     - baseRole, customRole                           — metadata
 *     - isLoading, isError                             — RTK state
 *
 * The underlying RTK query is tagged 'MyPermissions', so role/override
 * changes in the same session invalidate and refetch automatically
 * (see rbacApi.js — updateRole, assignRole, updateUserOverrides all
 * invalidate this tag).
 *
 * Owner short-circuit:
 *   Owners always return true for every `has()` check. This matches server
 *   behavior where DEFAULT_ROLE_PERMISSIONS.owner = ALL_PERMISSIONS.
 */
import { useMemo } from 'react';
import { useGetMyPermissionsQuery } from '@api/rbacApi';
import { useAuth } from './useAuth';

export const usePermissions = () => {
  const { user, isAuthenticated } = useAuth();
  const { data, isLoading, isError, error } = useGetMyPermissionsQuery(
    undefined,
    { skip: !isAuthenticated },
  );

  const permissions = useMemo(() => {
    if (!data?.permissions) return [];
    return Array.isArray(data.permissions) ? data.permissions : [];
  }, [data]);

  const permSet = useMemo(() => new Set(permissions), [permissions]);
  const baseRole = data?.baseRole || user?.role || null;
  const isOwner = baseRole === 'owner';

  const has = useMemo(() => (perm) => {
    if (!perm) return true;
    if (isOwner) return true;
    return permSet.has(perm);
  }, [permSet, isOwner]);

  const hasAny = useMemo(() => (perms) => {
    if (!perms || perms.length === 0) return true;
    if (isOwner) return true;
    return perms.some((p) => permSet.has(p));
  }, [permSet, isOwner]);

  const hasAll = useMemo(() => (perms) => {
    if (!perms || perms.length === 0) return true;
    if (isOwner) return true;
    return perms.every((p) => permSet.has(p));
  }, [permSet, isOwner]);

  return {
    permissions,
    permSet,
    baseRole,
    customRole: data?.customRole || null,
    isOwner,
    isLoading,
    isError,
    error,
    has,
    hasAny,
    hasAll,
  };
};

export default usePermissions;
